/**
 * ChatPage Component
 *
 * Thin orchestrator. A conversation can fork into branches; instead of showing
 * two side-by-side panels, we keep all branches mounted in a horizontal rail
 * and slide between them with a segmented toggle at the top of the card.
 */

import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import { useTenantPath } from '@/routing';
import { Sidebar } from '@/components/layout/Sidebar';
import { ConversationPanel } from '@modules/conversations/components/ConversationPanel';
import { useLanguage } from '@/hooks/useLanguage';
import { useKeyboardAwareViewport } from '@/hooks/useKeyboardAwareViewport';
import { useBrand } from '@/brand';
import conversationService from '@modules/conversations/services/conversationService';
import type { BranchResult, Conversation } from '@/types/models';
import './ChatPage.css';

/** Maximum number of split panels (Main + branches) shown side-by-side. */
const BRANCH_LIMIT = 3;
/** Minimum width (px) per split panel — below this the chat becomes unusable. */
const MIN_PANEL_WIDTH = 280;
/** sessionStorage key for the current panel layout. We persist only enough
 *  to remount panels (id, conversationId, label, etc.). Heavy payloads
 *  (branchResult, initialConversation) are dropped — the panel's own hooks
 *  re-fetch the conversation from the server.
 *
 *  We keep ONE entry, not one per conversation. On every panels change we
 *  overwrite this entry. On mount we restore only if the URL conversationId
 *  is part of the stored layout — which means: (a) page reload keeps the
 *  layout, (b) navigating to a different chat overwrites it on the next
 *  setBranches call, and (c) navigating back to a chat that's no longer
 *  in storage falls back to just Main. We can't key by conversationId
 *  because ConversationPanel rewrites the URL via replaceState as panels
 *  hydrate, so URL→storage mapping is unstable. */
const PANEL_STORAGE_KEY = 'chatPanelsLayout';

/** Read the stored panel layout. Returns null if nothing's stored, the
 *  stored data is malformed, or the URL conversationId isn't one of the
 *  stored panels (which means the user has navigated to a different chat
 *  since the layout was saved — show only Main, don't restore stale data). */
function readStoredPanels(currentConversationId: string | null | undefined): Branch[] | null {
  if (!currentConversationId || typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(PANEL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    // Only restore if the current URL conversation is part of the saved
    // layout. Any panel matching is enough — the user could have been
    // focused on a branch when they reloaded (URL = branch's id, but
    // panel 0 is still Main).
    const matches = (parsed as Branch[]).some(b => b.conversationId === currentConversationId);
    if (!matches) return null;
    return parsed as Branch[];
  } catch {
    return null;
  }
}

function writeStoredPanels(branches: Branch[]) {
  if (typeof window === 'undefined') return;
  try {
    // Strip non-serializable / heavy fields. The panel re-fetches its own
    // conversation/messages from the server when remounted.
    const minimal = branches.map(b => ({
      id: b.id,
      conversationId: b.conversationId,
      branchPointMessageId: b.branchPointMessageId,
      label: b.label,
      isStandalone: b.isStandalone,
    }));
    window.sessionStorage.setItem(PANEL_STORAGE_KEY, JSON.stringify(minimal));
  } catch {
    /* sessionStorage full / disabled — silently drop, this is best-effort */
  }
}

interface Branch {
  id: string;                                  // stable key for React
  conversationId?: string;                     // URL-driven main conversation
  branchResult?: BranchResult;                 // newly-created branch payload
  branchPointMessageId?: string;
  label: string;                               // "Main" / "Branch 2" / ...
  /** Pre-loaded conversation payload — set when this panel was opened via the
   *  sidebar "Open in split screen" action so ConversationPanel can hydrate
   *  immediately without a second fetch. */
  initialConversation?: Conversation;
  /** Marks panels that aren't real branches of the main thread but unrelated
   *  conversations opened side-by-side via the sidebar. The header shows the
   *  panel's own agent name (not "Branch N") so the user can tell which
   *  conversation belongs to which agent when comparing chats side-by-side.
   *  Still bound by BRANCH_LIMIT. */
  isStandalone?: boolean;
}

/**
 * BranchSwitcher — pill toggle with a single sliding "thumb" indicator.
 *
 * The thumb is an absolutely-positioned pseudo-pill that translates between
 * segments by measuring each segment's offsetLeft / offsetWidth. Page itself
 * does NOT slide on tab change; only this thumb animates.
 *
 * Mobile-only long-press: holding any pill for ~500ms opens a small popover
 * above it with a "Close branch" action. Closing is the same as desktop's
 * per-panel × button (calls `onClose(idx)` which delegates to the parent's
 * `handleClosePanel`). Tap-to-switch still works (long-press cancels the
 * subsequent click).
 */
function BranchSwitcher({
  branches,
  activeIdx,
  onChange,
  onClose,
  ariaLabel,
  closeLabel,
}: {
  branches: Branch[];
  activeIdx: number;
  onChange: (idx: number) => void;
  onClose?: (idx: number) => void;
  ariaLabel: string;
  closeLabel: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  // Long-press state machine:
  //   `pressedIdx`  → which pill is being HELD right now. Drives the
  //                   "warming to red" CSS animation while the finger is
  //                   down. Cleared on release / cancel / move > 8px.
  //   `armedIdx`    → which pill has been "armed" by a successful long-
  //                   press (held ≥ 300ms then released). The pill stays
  //                   red and shows an inline × icon. Tapping the × closes
  //                   the branch; tapping ANYWHERE else cancels (clears
  //                   armedIdx without doing anything).
  //   `closingIdx`  → which pill is mid-exit-animation. Used briefly
  //                   after the user taps the inline ×, before the panel
  //                   is removed from the rail.
  // suppressClickRef prevents the synthetic click after a long-press from
  // reaching handleSegClick (which would otherwise switch panels).
  const [pressedIdx, setPressedIdx] = useState<number | null>(null);
  const [armedIdx, setArmedIdx] = useState<number | null>(null);
  const [closingIdx, setClosingIdx] = useState<number | null>(null);
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressStartRef = useRef<{ x: number; y: number } | null>(null);
  const suppressClickRef = useRef(false);

  // Re-measure whenever the active index, the branch list, or the viewport
  // changes (font fallback shift / orientation flip can resize segments).
  useEffect(() => {
    const measure = () => {
      const el = containerRef.current;
      if (!el) return;
      const segs = el.querySelectorAll<HTMLButtonElement>('.branch-switcher-seg');
      const active = segs[activeIdx];
      if (!active) return;
      setThumb({ left: active.offsetLeft, width: active.offsetWidth });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activeIdx, branches.length]);

  // Auto-dismiss the armed state after 6s of no interaction — long enough
  // for a deliberate "release, look, tap ×" flow but short enough that a
  // forgotten armed pill doesn't linger forever.
  useEffect(() => {
    if (armedIdx === null) return;
    const t = setTimeout(() => setArmedIdx(null), 6000);
    return () => clearTimeout(t);
  }, [armedIdx]);

  // ANY tap outside the armed pill cancels the close intent. This
  // includes tapping any other pill, the message area, the input bar —
  // basically anywhere that isn't the small × icon. The × itself stops
  // propagation so its tap reaches the close handler before this fires.
  useEffect(() => {
    if (armedIdx === null) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Element;
      // If the tap was on the inline close icon of the armed pill, let
      // it through to the icon's own onClick. Otherwise cancel.
      if (target.closest('.branch-switcher-seg-close')) return;
      setArmedIdx(null);
    };
    // pointerdown fires before click, so we capture the tap-outside intent
    // before the click handler on a non-armed pill runs (which would
    // otherwise switch branches before we cleared armedIdx).
    document.addEventListener('pointerdown', handler, true);
    return () => {
      document.removeEventListener('pointerdown', handler, true);
    };
  }, [armedIdx]);

  // ---- Press / long-press handlers ----
  // Flow:
  //   1. PointerDown → start the red-warming animation on the pill, mark
  //      the press start time.
  //   2. At 300ms, fire a haptic pulse — feedback that the user has held
  //      long enough for "close" intent (the pill is now noticeably red).
  //   3. PointerUp:
  //         held >= 300ms → SHOW the close popover, suppress the click
  //                         (this is the "release reveals close" gesture).
  //         held <  300ms → treat as normal tap → switch to that branch.
  //   4. PointerMove > 8px or PointerCancel → bail out of the gesture.
  //
  // We use pointer events so a single set of handlers covers touch + mouse.
  const HOLD_THRESHOLD_MS = 300;
  const pressStartTimeRef = useRef<number>(0);
  const pressedIdxRef = useRef<number | null>(null);

  const startPress = (e: React.PointerEvent<HTMLButtonElement>, idx: number) => {
    if (!onClose) return;
    pressStartRef.current = { x: e.clientX, y: e.clientY };
    pressStartTimeRef.current = Date.now();
    pressedIdxRef.current = idx;
    setPressedIdx(idx); // triggers the "warming to red" CSS animation
    // Schedule a haptic pulse at the threshold so the user feels when
    // their hold has "armed" the close gesture. Cancelled if they
    // release / move before then.
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        try { navigator.vibrate(25); } catch { /* permissions / disabled */ }
      }
    }, HOLD_THRESHOLD_MS);
  };

  const cancelPress = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    pressStartRef.current = null;
    pressedIdxRef.current = null;
    setPressedIdx(null); // resets the red-warming animation
  };

  const endPress = () => {
    const idx = pressedIdxRef.current;
    const heldFor = Date.now() - pressStartTimeRef.current;
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    pressStartRef.current = null;
    pressedIdxRef.current = null;
    setPressedIdx(null); // stop the warming animation

    if (idx !== null && heldFor >= HOLD_THRESHOLD_MS && onClose) {
      // Held long enough: arm the close intent. The pill stays red and
      // an inline × icon appears inside it. Tapping the × commits the
      // close; tapping anywhere else cancels.
      suppressClickRef.current = true;
      setArmedIdx(idx);
      // Confirmation tick — pill is armed, × is now visible.
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        try { navigator.vibrate(15); } catch { /* permissions / disabled */ }
      }
    }
    // < HOLD_THRESHOLD_MS → fall through to the click handler which runs
    // after pointerup and switches the active panel as usual.
  };

  const movePress = (e: React.PointerEvent<HTMLButtonElement>) => {
    // If the finger drifts more than ~8px, cancel — the user is scrolling
    // or starting a swipe, not pressing.
    const start = pressStartRef.current;
    if (!start) return;
    const dx = Math.abs(e.clientX - start.x);
    const dy = Math.abs(e.clientY - start.y);
    if (dx > 8 || dy > 8) cancelPress();
  };

  const handleSegClick = (i: number) => {
    if (suppressClickRef.current) {
      // endPress just armed this pill; eat the synthetic click that
      // follows so we don't immediately re-switch panels.
      suppressClickRef.current = false;
      return;
    }
    // The outside-tap pointerdown handler has already cleared armedIdx
    // by the time this click handler fires (pointerdown captures before
    // click bubbles). So at this point we just do the normal switch.
    onChange(i);
  };

  const handleConfirmClose = (idx: number, e?: React.MouseEvent | React.PointerEvent) => {
    // Stop propagation so the document-level outside-tap handler (which
    // runs on pointerdown capture) doesn't see this tap as "outside" and
    // cancel the armed state before our state update lands.
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    // Trigger the exit animation on the pill (red fade), then actually
    // remove the panel after the animation completes. 220ms matches the
    // CSS animation duration in `.branch-switcher-seg.is-closing`.
    setArmedIdx(null);
    setClosingIdx(idx);
    // Haptic confirmation — short tick to acknowledge the action fired.
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(15); } catch { /* permissions / disabled */ }
    }
    setTimeout(() => {
      setClosingIdx(null);
      if (onClose) onClose(idx);
    }, 220);
  };

  // Disable close when only a single panel is open (closing the last would
  // leave an empty rail). Same guard as `handleClosePanel` on the parent.
  const canClose = branches.length > 1;

  return (
    <div
      ref={containerRef}
      className="branch-switcher"
      role="tablist"
      aria-label={ariaLabel}
    >
      <span
        className="branch-switcher-pill"
        aria-hidden="true"
        style={{ left: thumb.left, width: thumb.width }}
      />
      {branches.map((branch, i) => {
        const isArmed = armedIdx === i;
        const classes = [
          'branch-switcher-seg',
          activeIdx === i ? 'active' : '',
          pressedIdx === i ? 'is-pressing' : '',
          isArmed ? 'is-armed' : '',
          closingIdx === i ? 'is-closing' : '',
        ].filter(Boolean).join(' ');
        return (
          <button
            key={branch.id}
            type="button"
            role="tab"
            aria-selected={activeIdx === i}
            className={classes}
            onClick={() => handleSegClick(i)}
            onPointerDown={(e) => startPress(e, i)}
            onPointerUp={endPress}
            onPointerCancel={cancelPress}
            onPointerLeave={cancelPress}
            onPointerMove={movePress}
            onContextMenu={(e) => e.preventDefault()}
          >
            <span className="branch-switcher-seg-label" dir="auto">{branch.label}</span>
            {isArmed && canClose && (
              <span
                className="branch-switcher-seg-close"
                role="button"
                aria-label={closeLabel}
                title={closeLabel}
                onPointerDown={(e) => {
                  // Stop the document-level outside-tap handler (which is
                  // also a pointerdown listener) from seeing this as
                  // "outside" — without this, armedIdx is cleared a tick
                  // before our click handler fires, and the close never
                  // commits.
                  e.stopPropagation();
                }}
                onClick={(e) => handleConfirmClose(i, e)}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function ChatPage() {
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  /* White-label gate: when off (the platform default), the chat keeps the
     existing neutral white surface and the typing bar uses its original
     border. M² preset turns this on, which activates the agent-tinted
     `[data-tint-by-agent="true"]` selectors in ChatPage.css. */
  const brand = useBrand();
  const tintByAgent = !!brand.chat?.tintByAgent;

  // Mobile keyboard handling: locks the body so iOS can't auto-scroll the
  // page off-screen when an input is focused, publishes --chat-vvh from
  // window.visualViewport so the chat surface tracks the visible area,
  // and bumps `.chat-messages-container` scrollTop by the viewport delta
  // on every keyboard frame so the latest message stays just above the
  // keyboard. Same behavior on Android Chrome and iOS Safari.
  useKeyboardAwareViewport();

  const conversationIdParam = searchParams.get('conversation');
  const botIdParam = searchParams.get('bot');
  /** Set when the user picked an agent from a project page — the conversation
   *  they're about to start belongs to that shared project. */
  const projectIdParam = searchParams.get('project');
  /** Only meaningful alongside `project`, and only until the thread exists. */
  const threadModeParam = searchParams.get('mode') === 'solo' ? 'solo' as const : undefined;
  const autoBranchParam = searchParams.get('autoBranch');
  /** Message to scroll to on arrival — set when a handoff card teleported us
   *  here, so we land on the twin card instead of at the bottom. */
  const focusMessageParam = searchParams.get('focusMessage');

  // Branches state — starts with a single "Main" branch from the URL,
  // EXCEPT on page reload where we restore the panel layout that was
  // visible right before the reload. Restore is gated on the URL's
  // conversationId being part of the stored layout — soft navigation
  // to a different chat overwrites the storage, so re-mounting a chat
  // that's no longer the saved one falls through to just Main.
  const [branches, setBranches] = useState<Branch[]>(() => {
    const stored = readStoredPanels(conversationIdParam);
    if (stored && stored.length > 0) return stored;
    return [{
      id: conversationIdParam || botIdParam || 'new',
      conversationId: conversationIdParam || undefined,
      label: t('chat.branchMain') || 'Main',
    }];
  });
  const [activeIdx, setActiveIdx] = useState(0);

  /** Which message a teleport should land on, and in which conversation.
   *  Held as state (not fired as an event) so the request survives until the
   *  destination panel has actually rendered that message — on a teleport that
   *  navigates, the panel is still fetching when the jump is made. `nonce`
   *  makes a repeat jump to the same message re-fire. */
  const [focusRequest, setFocusRequest] =
    useState<{ conversationId: string; messageId: string; nonce: number } | null>(null);
  const focusNonceRef = useRef(0);
  const requestFocus = useCallback((conversationId: string, messageId: string) => {
    focusNonceRef.current += 1;
    setFocusRequest({ conversationId, messageId, nonce: focusNonceRef.current });
  }, []);

  // Persist the visible-panel layout on every change so a page refresh can
  // restore exactly what the user was looking at. The single sessionStorage
  // entry gets overwritten on every change — including the URL-change reset
  // below that drops us back to [{Main}], which is correct (navigating to a
  // different chat shouldn't restore old panels on its next reload).
  useEffect(() => {
    writeStoredPanels(branches);
  }, [branches]);

  // Split view = desktop AND multiple branches. Drives both the side-by-side
  // layout AND the per-panel "Branch N" labeling. Mobile always renders one
  // panel at a time, so it stays single-view regardless of branch count.
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 769px)').matches : true
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 769px)');
    const sync = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  const isSplitView = isDesktop && branches.length > 1;

  // Per-panel widths in pixels — null means "evenly distributed (default flex
  // 1 1 0 layout)". Once the user grabs the divider we switch to explicit
  // pixel widths so the drag is precise and stable. Reset to null on
  // open/close so new panels get an even share.
  const [panelWidths, setPanelWidths] = useState<number[] | null>(null);
  const railRef = useRef<HTMLDivElement>(null);

  // Per-panel agent colors — populated as each panel emits panelBotLoaded.
  // We can't recompute agentColor on focus change from a one-shot event, so
  // we cache every panel's color and look up by the active index.
  const [panelColors, setPanelColors] = useState<Record<string, string>>({});
  useEffect(() => {
    const handler = (e: CustomEvent<{ panelId: string; avatarColor: string }>) => {
      if (!e.detail.panelId.startsWith('branch-')) return;
      setPanelColors(prev => ({ ...prev, [e.detail.panelId]: e.detail.avatarColor || 'blue' }));
    };
    window.addEventListener('panelBotLoaded', handler as EventListener);
    return () => window.removeEventListener('panelBotLoaded', handler as EventListener);
  }, []);
  const agentColor = panelColors[`branch-${activeIdx}`] || 'blue';

  // Reset branches when the URL-driven conversation changes — but only on
  // a genuine navigation to a DIFFERENT chat. ConversationPanel calls
  // window.history.replaceState as each panel hydrates, so the URL's
  // conversation param can change to a sibling panel's id without the
  // user actually navigating anywhere. Treat URL changes that point to a
  // conversation already in the visible layout as panel-driven, not as
  // navigation, and skip the reset.
  const lastUrlKeyRef = useRef<string>(conversationIdParam || botIdParam || 'new');
  useEffect(() => {
    const urlKey = conversationIdParam || botIdParam || 'new';
    if (urlKey === lastUrlKeyRef.current) return;
    // If the new URL conversationId matches one of the panels we already
    // have, this was a panel-driven replaceState — keep the layout, just
    // update the ref so we don't keep firing.
    if (conversationIdParam && branches.some(b => b.conversationId === conversationIdParam)) {
      lastUrlKeyRef.current = urlKey;
      return;
    }
    lastUrlKeyRef.current = urlKey;
    setBranches([{
      id: urlKey,
      conversationId: conversationIdParam || undefined,
      label: t('chat.branchMain') || 'Main',
    }]);
    setActiveIdx(0);
    // Drop cached colors from the previous conversation — otherwise stale
    // entries (e.g. branch-2 from the old chat) leak across navigations.
    setPanelColors({});
  }, [conversationIdParam, botIdParam, t, branches]);

  // NOTE: previously this effect auto-hydrated existing branches from the
  // backend when opening a conversation, so a chat with N children would
  // mount with N+1 panels visible. The user found that surprising — opening
  // a chat shouldn't dump all its branches into the split view. Now the
  // page mounts with only Main; the user explicitly opens branches via
  // the fork button or the sidebar three-dot "Open in split screen".

  // Silent URL update — keeps ConversationPanel mounted with its loaded bot/messages.
  // Using navigate() here would re-trigger the useSearchParams → branches-reset
  // → ConversationPanel remount cycle, dropping the bot context and rendering
  // "General Chat" until the conversation reloads.
  const handleNavigate = useCallback((path: string) => {
    // Silently update the address bar (no react-router navigation → no panel
    // remount) so the conversation is reflected in the URL. Must be a REAL
    // BrowserRouter path (tenant-prefixed, no `#`) — a hash path would be
    // dropped to `/` on refresh and bounce the user to home.
    window.history.replaceState(null, '', tenantPath(path));
  }, [tenantPath]);

  // Branch-cap warning, with a mobile-only long-press hint so users
  // discover the close gesture (mobile has no visible × on each panel).
  const showBranchLimitWarning = useCallback(() => {
    const baseMsg = t('chat.branchLimitReached') ||
      'You have 3 branches open. Close one to open a new branch.';
    const isMobile = typeof window !== 'undefined'
      && window.matchMedia('(max-width: 768px)').matches;
    const hint = isMobile
      ? (' ' + (t('chat.branchLongPressHint') || 'Long-press a branch tab to close it.'))
      : '';
    message.warning(baseMsg + hint);
  }, [t]);

  // Listen for branch-create requests from a message fork icon.
  //
  // Cap behavior on the FORK path: when BRANCH_LIMIT panels are already
  // visible, we BAIL OUT and toast the user — the user must close one of
  // the open branches first. (The fork buttons are also rendered disabled
  // via branchDisabled plumbing, so this guard only catches programmatic /
  // race-y triggers like the autoBranch URL handoff.) Main (idx 0) is
  // always panel 0, branches grow rightward.
  //
  // NB: the sidebar "Open in split screen" path lives in a separate listener
  // below and uses *silent replacement* at the cap — that's the user's
  // intended behavior for that path. Don't unify the two.
  useEffect(() => {
    const handler = async (event: CustomEvent<{
      conversationId: string;
      messageId: string;
    }>) => {
      const { conversationId, messageId } = event.detail;

      // Read the latest branches synchronously from the setter so we don't
      // race with a state-update from a click that happened during render.
      let atCap = false;
      setBranches(prev => {
        atCap = prev.length >= BRANCH_LIMIT;
        return prev;
      });
      if (atCap) {
        showBranchLimitWarning();
        return;
      }

      try {
        const result = await conversationService.createBranch(conversationId, messageId);

        window.dispatchEvent(new CustomEvent('branchCreated', { detail: { result } }));

        setBranches(prev => {
          // Re-check the cap inside the setter — between the initial guard
          // and now another panel could have appeared (sidebar replacement,
          // hydration). If we're at the cap now, bail and toast.
          if (prev.length >= BRANCH_LIMIT) {
            showBranchLimitWarning();
            return prev;
          }
          const branchLabel = t('chat.branchLabel') || 'Branch';
          const newPanel: Branch = {
            id: result.conversation.conversation_id,
            // Also record conversationId — writeStoredPanels persists this but
            // NOT branchResult (too heavy). Without it a reload restores the
            // panel with no conversation to fetch and renders an empty chat.
            conversationId: result.conversation.conversation_id,
            branchResult: result,
            branchPointMessageId: result.branch_point_message_id,
            label: `${branchLabel} ${prev.length}`,
          };
          const nextBranches = [...prev, newPanel];
          setActiveIdx(prev.length);
          // Reset any drag-locked widths so the new layout shows even thirds
          // — otherwise the existing pixel widths can sum to the rail width
          // with no room for the new panel, or leave a stale ratio that no
          // longer matches the panel set.
          setPanelWidths(null);
          return nextBranches;
        });
      } catch (error) {
        console.error('[ChatPage] Branch creation failed:', error);
      }
    };

    window.addEventListener('branchRequested', handler as unknown as EventListener);
    return () => window.removeEventListener('branchRequested', handler as unknown as EventListener);
  }, [t]);

  // Listen for agent-handoff requests from a message's "continue with another
  // agent" button. Mirrors the fork listener above — same cap, same rightward
  // growth — but the new panel is labelled with the TARGET AGENT's name rather
  // than "Branch N", because which agent took over is the whole point.
  useEffect(() => {
    const handler = async (event: CustomEvent<{
      conversationId: string;
      messageId: string;
      targetChatbotId: string;
      targetChatbotName?: string;
      openMode?: 'jump' | 'split';
    }>) => {
      const { conversationId, messageId, targetChatbotId, targetChatbotName } = event.detail;
      // 'jump' is the default: a handoff normally REPLACES the view, the same
      // as opening any other conversation. Split is the deliberate opt-in for
      // when the user wants to see both agents at once.
      const openMode = event.detail.openMode === 'split' ? 'split' : 'jump';
      if (!conversationId || !messageId || !targetChatbotId) return;

      // Only the split path consumes a panel, so only it can hit the cap.
      if (openMode === 'split') {
        let atCap = false;
        setBranches(prev => {
          atCap = prev.length >= BRANCH_LIMIT;
          return prev;
        });
        if (atCap) {
          showBranchLimitWarning();
          return;
        }
      }

      try {
        const result = await conversationService.branchToAgent(
          conversationId, messageId, targetChatbotId
        );

        // Sidebar: register the new conversation and bump the parent's count.
        window.dispatchEvent(new CustomEvent('branchCreated', { detail: { result } }));

        // The origin thread just gained an outbound card server-side. Push it
        // into the origin panel directly — without this the link back only
        // appears after a reload, which reads as "the handoff didn't record".
        // (On a jump the origin panel unmounts, and the card is re-fetched from
        // the server the next time the user opens that thread.)
        if (result.origin_marker) {
          window.dispatchEvent(new CustomEvent('handoffMarkerAdded', {
            detail: { conversationId, message: result.origin_marker },
          }));
        }

        const newConversationId = result.conversation.conversation_id;

        if (openMode === 'jump') {
          // Land on the inbound card so the arrival explains itself: the last
          // copied message IS that card.
          const inbound = result.messages[result.messages.length - 1];
          const query = inbound?.message_id
            ? `/chat?conversation=${newConversationId}&focusMessage=${inbound.message_id}`
            : `/chat?conversation=${newConversationId}`;
          navigate(tenantPath(query));
          return;
        }

        setBranches(prev => {
          if (prev.length >= BRANCH_LIMIT) {
            showBranchLimitWarning();
            return prev;
          }
          const newPanel: Branch = {
            id: result.conversation.conversation_id,
            // See the fork path: conversationId is what survives into
            // sessionStorage, so a reload can re-fetch this thread.
            conversationId: result.conversation.conversation_id,
            branchResult: result,
            branchPointMessageId: result.branch_point_message_id,
            label: targetChatbotName
              || result.conversation.chatbot_name
              || (t('chat.branchLabel') || 'Branch'),
          };
          setActiveIdx(prev.length);
          setPanelWidths(null);
          return [...prev, newPanel];
        });
      } catch (error) {
        console.error('[ChatPage] Agent handoff failed:', error);
        message.error(t('chat.agentBranch.failed') || 'Could not continue with that agent.');
      }
    };

    window.addEventListener('agentBranchRequested', handler as unknown as EventListener);
    return () => window.removeEventListener('agentBranchRequested', handler as unknown as EventListener);
  }, [t, showBranchLimitWarning, navigate, tenantPath]);

  // Teleport across a handoff card.
  //
  //   • Target already on screen → just focus that panel. Opening a second
  //     copy of a conversation the user can already see would be baffling.
  //   • Otherwise → navigate to it. `focusMessage` carries the twin marker's
  //     id so we land ON the card rather than at the bottom of the thread,
  //     which is what makes this feel like returning to a bookmark.
  useEffect(() => {
    const handler = (event: CustomEvent<{ conversationId: string; messageId?: string }>) => {
      const { conversationId, messageId } = event.detail;
      if (!conversationId) return;

      let foundIdx = -1;
      setBranches(prev => {
        foundIdx = prev.findIndex(b =>
          (b.conversationId || b.branchResult?.conversation.conversation_id) === conversationId
        );
        return prev;
      });

      if (foundIdx >= 0) {
        setActiveIdx(foundIdx);
        if (messageId) requestFocus(conversationId, messageId);
        return;
      }

      const query = messageId
        ? `/chat?conversation=${conversationId}&focusMessage=${messageId}`
        : `/chat?conversation=${conversationId}`;
      navigate(tenantPath(query));
    };

    window.addEventListener('handoffTeleport', handler as unknown as EventListener);
    return () => window.removeEventListener('handoffTeleport', handler as unknown as EventListener);
  }, [navigate, tenantPath, requestFocus]);

  // After a teleport NAVIGATION lands, re-emit the scroll request as an event
  // and strip the param, so a later reload doesn't yank the user back to an
  // old anchor. The ref makes this fire once per distinct target under
  // StrictMode's double-invoke, exactly like the autoBranch handoff below.
  const handledFocusMessageRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusMessageParam || !conversationIdParam) return;
    if (handledFocusMessageRef.current === focusMessageParam) return;
    handledFocusMessageRef.current = focusMessageParam;
    const messageId = focusMessageParam;
    const conversationId = conversationIdParam;
    navigate(tenantPath(`/chat?conversation=${conversationId}`), { replace: true });
    // No delay needed: the request is state, so the panel picks it up whenever
    // its messages finish loading.
    requestFocus(conversationId, messageId);
  }, [focusMessageParam, conversationIdParam, navigate, tenantPath, requestFocus]);

  /** Close a panel by index. Any panel — including Main (idx 0) — can be
   *  closed in split view. Closing Main re-indexes the remaining branches
   *  so the leftmost surviving branch becomes the new "first" panel and
   *  shows its agent name (panelLabel becomes undefined for i === 0).
   *  When dropping back to a single panel we also clear panelColors entries
   *  that no longer belong to a mounted panel so a future split doesn't
   *  inherit a stale tint. */
  const handleClosePanel = useCallback((idx: number) => {
    setBranches(prev => {
      if (idx < 0 || idx >= prev.length) return prev;
      // Don't allow closing the very last panel — that would leave the user
      // with an empty rail. The X is hidden in single-pane mode anyway, so
      // this is a defensive guard against re-entry / race conditions.
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== idx);
      setActiveIdx(curr => {
        if (curr === idx) return Math.max(0, idx - 1);
        if (curr > idx) return curr - 1;
        return curr;
      });
      // Reset panel widths so remaining panels redistribute the available
      // space evenly — otherwise the closed panel's slot leaves a gap.
      setPanelWidths(null);
      return next;
    });
    setPanelColors(prev => {
      const next: Record<string, string> = {};
      // After removal the remaining panels are re-indexed; we can't safely
      // remap colors by ID, so just drop the cache and let the per-panel
      // panelBotLoaded events repopulate it.
      void prev; // intentionally unused
      return next;
    });
  }, []);

  // Listen for "Open in split screen" requests from the sidebar three-dot menu.
  //   • not split (1 panel)            → append the chosen chat as 2nd panel
  //   • already split, < BRANCH_LIMIT  → append as the new rightmost panel
  //   • already split, >= BRANCH_LIMIT → REJECT and warn — the user must
  //     actively close one of the open panels first. We never silently
  //     replace a panel; the user is in charge of which panels close.
  useEffect(() => {
    const handler = async (event: CustomEvent<{ conversationId: string }>) => {
      const { conversationId } = event.detail;
      if (!conversationId) return;

      // Pre-check the cap synchronously (and also re-check inside the setter
      // below to handle races). Showing the warning here gives the user
      // immediate feedback without paying for a getConversation round-trip
      // that we'd then throw away.
      let atCap = false;
      let openIdx = -1;
      setBranches(prev => {
        atCap = prev.length >= BRANCH_LIMIT;
        openIdx = prev.findIndex(b =>
          (b.conversationId || b.branchResult?.conversation.conversation_id) === conversationId);
        return prev;
      });
      // Already on screen: FOCUS it rather than doing nothing. The silent
      // no-op here read as a broken button — most visibly from a handoff
      // card, where both threads are routinely open already, so "Split
      // screen" appeared to do nothing at all.
      if (openIdx >= 0) {
        setActiveIdx(openIdx);
        return;
      }
      if (atCap) {
        showBranchLimitWarning();
        return;
      }

      // Mount the pane IMMEDIATELY, then let it load itself.
      //
      // This used to `await getConversation(...)` first and pass the result in
      // as `initialConversation`, saving the panel one fetch — but it meant
      // nothing appeared on screen until that round trip came back, so the
      // button felt unresponsive. The panel already fetches its own
      // conversation and messages on mount, so the pre-fetch bought a
      // marginal saving at the cost of the only latency the user can feel.
      const branchLabel = t('chat.branchLabel') || 'Branch';
      setBranches(prev => {
        // Re-check inside the setter — between the pre-check and now
        // another panel could have appeared (a fork in flight, etc.).
        if (prev.some(b => (b.conversationId || b.branchResult?.conversation.conversation_id) === conversationId)) {
          return prev;
        }
        if (prev.length >= BRANCH_LIMIT) {
          showBranchLimitWarning();
          return prev;
        }
        const newPanel: Branch = {
          id: conversationId,
          conversationId,
          label: `${branchLabel} ${prev.length}`,
          isStandalone: true,
        };
        const nextBranches = [...prev, newPanel];
        setActiveIdx(prev.length);
        // Reset widths so the new panel gets an even share.
        setPanelWidths(null);
        return nextBranches;
      });
    };

    window.addEventListener('openSplitScreen', handler as unknown as EventListener);
    return () => window.removeEventListener('openSplitScreen', handler as unknown as EventListener);
  }, [t]);

  // When MainPage hands off a branch request via ?autoBranch=<msgId>, fire the
  // branchRequested event once we've mounted (and strip the param from the
  // URL so it doesn't re-fire on every render).
  //
  // The ref guards against React StrictMode's double-effect-invoke on mount
  // (setup → cleanup → setup): without it, the autoBranch param fires the
  // event twice and the user lands on /chat with TWO branches created from
  // a single click on the home-page chat. The ref survives the strict-mode
  // cycle because it's tied to the component instance, not the effect.
  const handledAutoBranchRef = useRef<string | null>(null);
  useEffect(() => {
    if (!autoBranchParam || !conversationIdParam) return;
    if (handledAutoBranchRef.current === autoBranchParam) return;
    handledAutoBranchRef.current = autoBranchParam;
    const messageId = autoBranchParam;
    const conversationId = conversationIdParam;
    navigate(tenantPath(`/chat?conversation=${conversationId}`), { replace: true });
    window.dispatchEvent(new CustomEvent('branchRequested', {
      detail: { conversationId, messageId, sourcePanel: 'branch-0' },
    }));
  }, [autoBranchParam, conversationIdParam, navigate, tenantPath]);

  // Broadcast active branch to sidebar so the right item highlights
  useEffect(() => {
    const active = branches[activeIdx];
    const convId = active?.conversationId
      || active?.branchResult?.conversation.conversation_id
      || null;
    window.dispatchEvent(new CustomEvent('splitScreenChanged', {
      detail: { conversationId: convId },
    }));
  }, [branches, activeIdx]);

  // Guard: /chat without ?conversation= and without ?bot= would mount a panel
  // with no convo and no bot, which is the only way to land on the no-agent
  // "general" chat surface. That surface isn't part of the user-facing product,
  // so bounce to the agent picker instead.
  useEffect(() => {
    if (!conversationIdParam && !botIdParam) {
      navigate(tenantPath('/'), { replace: true });
    }
  }, [conversationIdParam, botIdParam, navigate, tenantPath]);

  const activeBranch = branches[activeIdx];
  const activeConvId = activeBranch?.conversationId
    || activeBranch?.branchResult?.conversation.conversation_id;

  // Fork buttons are disabled across ALL panels and all messages whenever
  // we've hit the visible-panel cap. The user must close a branch first.
  // (The cap is BRANCH_LIMIT total panels including Main — once branches.length
  // reaches BRANCH_LIMIT, adding another branch would push us past the cap.)
  const branchDisabled = branches.length >= BRANCH_LIMIT;
  const branchDisabledReason = t('chat.branchLimitTooltip')
    || 'Close one of the open branches to fork from this message.';

  // Mobile-only: horizontal swipe on the rail navigates between branches.
  // Implemented as a native touch listener on the rail element so we can
  // call preventDefault() once we've decided the gesture is horizontal —
  // React's synthetic touch events are passive and can't preventDefault
  // reliably. We attach/detach when isDesktop changes so desktop never
  // pays the listener cost.
  useEffect(() => {
    if (isDesktop) return;
    const rail = railRef.current;
    if (!rail || branches.length <= 1) return;

    let startX = 0;
    let startY = 0;
    let trackingDirection: 'unknown' | 'horizontal' | 'vertical' = 'unknown';
    let startedFromInteractive = false;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      // Don't hijack if the user is touching the input bar, the chat header
      // (long-press lives there), or any control with its own touch logic.
      const target = e.target as Element;
      startedFromInteractive = !!(target.closest?.(
        '.chat-input-container, .simple-chat-header, .branch-switcher, button, input, textarea, [contenteditable="true"]'
      ));
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      trackingDirection = 'unknown';
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startedFromInteractive) return;
      if (e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      // First ~10px of movement decides which axis we're on. Lock it in
      // so a slight diagonal mid-swipe doesn't flip us back to vertical.
      if (trackingDirection === 'unknown') {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        trackingDirection = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
      }
      if (trackingDirection === 'horizontal') {
        // Block the browser's default horizontal scroll / overscroll while
        // we own the gesture.
        if (e.cancelable) e.preventDefault();
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (startedFromInteractive) return;
      if (trackingDirection !== 'horizontal') return;
      const endX = e.changedTouches[0].clientX;
      const dx = endX - startX;
      const threshold = Math.max(50, rail.offsetWidth * 0.15);
      if (Math.abs(dx) < threshold) return;

      // RTL: visually swiping left should still feel like "next" in the
      // branch order. Flip the sign in RTL so the natural-direction logic
      // matches what the user sees on screen.
      const isRTL = document.dir === 'rtl' ||
        document.documentElement.getAttribute('dir') === 'rtl';
      const direction = isRTL ? -Math.sign(dx) : Math.sign(dx);
      // Negative direction (swipe right→left in LTR) advances to the next
      // branch; positive (left→right) goes back. Mirrors how iOS and
      // most chat apps interpret horizontal swipe between siblings.
      setActiveIdx(prev => {
        const next = direction < 0 ? prev + 1 : prev - 1;
        if (next < 0 || next >= branches.length) return prev;
        return next;
      });
    };

    // Use { passive: false } on touchmove so preventDefault works.
    rail.addEventListener('touchstart', onTouchStart, { passive: true });
    rail.addEventListener('touchmove', onTouchMove, { passive: false });
    rail.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      rail.removeEventListener('touchstart', onTouchStart);
      rail.removeEventListener('touchmove', onTouchMove);
      rail.removeEventListener('touchend', onTouchEnd);
    };
  }, [isDesktop, branches.length]);

  /**
   * Begin resizing the divider between panels at index `dividerIdx` (sits
   * between panels[dividerIdx] and panels[dividerIdx+1]).
   *
   * Mid-drag we update only the two adjacent panel widths — other panels
   * keep whatever width they had (or fall back to evenly-distributed if
   * panelWidths is null). RTL flips the drag axis so a leftward drag in
   * Hebrew shrinks the leading panel, just like LTR.
   */
  const beginDividerDrag = useCallback((dividerIdx: number, e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const rail = railRef.current;
    if (!rail) return;
    const railRect = rail.getBoundingClientRect();

    // Initial widths — measure live DOM so the drag starts from whatever the
    // panels are currently sized at, regardless of whether we're in
    // even-distribution mode (panelWidths === null) or pixel-locked mode.
    const slides = rail.querySelectorAll<HTMLDivElement>('.branch-slide');
    const startWidths: number[] = [];
    slides.forEach(s => startWidths.push(s.getBoundingClientRect().width));

    if (dividerIdx < 0 || dividerIdx >= startWidths.length - 1) return;

    const startX = e.clientX;
    const isRTL = document.dir === 'rtl' ||
      document.documentElement.getAttribute('dir') === 'rtl';

    const onMove = (ev: MouseEvent) => {
      // In RTL the visual "leading" edge is on the right, so the sign of the
      // delta needs to flip — moving the cursor left should still grow the
      // leading panel and shrink the trailing one in the user's mental model.
      const dx = (ev.clientX - startX) * (isRTL ? -1 : 1);
      const leftStart = startWidths[dividerIdx];
      const rightStart = startWidths[dividerIdx + 1];
      let leftNew = leftStart + dx;
      let rightNew = rightStart - dx;
      // Clamp both to the minimum panel width.
      if (leftNew < MIN_PANEL_WIDTH) {
        rightNew -= (MIN_PANEL_WIDTH - leftNew);
        leftNew = MIN_PANEL_WIDTH;
      }
      if (rightNew < MIN_PANEL_WIDTH) {
        leftNew -= (MIN_PANEL_WIDTH - rightNew);
        rightNew = MIN_PANEL_WIDTH;
      }
      // Don't exceed the rail's available width.
      if (leftNew + rightNew > railRect.width) return;
      const next = [...startWidths];
      next[dividerIdx] = leftNew;
      next[dividerIdx + 1] = rightNew;
      setPanelWidths(next);
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  return (
    <div className="chat-layout">
      <Sidebar
        options={{
          showHome: true,
          showChat: false,
          showBots: true,
          showSearch: true,
          showAdmin: true,
          showGroupAdmin: true,
          showSuperAdmin: true,
          showConversations: true,
          showBotsList: true,
          activeItem: 'chat',
          activeConversationId: activeConvId,
        }}
      />

      <main
        className={`chat-main-content ${branches.length > 1 ? 'has-branches' : ''}`}
        data-agent-color={agentColor}
        data-tint-by-agent={tintByAgent ? 'true' : undefined}
      >
        {/* Branch switcher — compact pill at the top of the card. The active
            background is a separate sliding pill (.branch-switcher-pill) that
            measures the active segment and animates left/width. Switching
            branches itself is instant (no rail slide), so all motion lives
            inside the toggle. */}
        {branches.length > 1 && (
          <BranchSwitcher
            branches={branches}
            activeIdx={activeIdx}
            onChange={setActiveIdx}
            onClose={handleClosePanel}
            ariaLabel={t('chat.branchesLabel') || 'Branches'}
            closeLabel={t('chat.closeBranch') || 'Close branch'}
          />
        )}

        {/* Branch rail —
            • Desktop: all branches render side-by-side (split layout). The
              switcher above doubles as a "focus" indicator for the agent
              tint, but every panel stays interactive.
            • Mobile: panels stack via horizontal translation (left offset),
              and the switcher slides between them. We keep `left` (not
              `transform`) so mobile's fixed chat input stays viewport-
              anchored. CSS overrides `left` to 0 on desktop. */}
        <div className="branch-rail" ref={railRef}>
          <div
            className="branch-rail-track"
            style={{
              ['--branch-count' as any]: branches.length,
              ['--active-idx' as any]: activeIdx,
            }}
          >
            {branches.map((branch, i) => {
              // Width strategy:
              //   • Mobile keeps the legacy translate-x-100% layout (CSS
              //     overrides flex-basis) so we don't apply explicit widths
              //     there — they'd fight the mobile rule.
              //   • Desktop, panelWidths set    → explicit pixel widths, drag-
              //     locked.
              //   • Desktop, panelWidths null   → flex 1 1 0, evenly shared.
              const widthStyle = (isSplitView && panelWidths && panelWidths[i] != null)
                ? { flex: `0 0 ${panelWidths[i]}px`, width: `${panelWidths[i]}px` }
                : undefined;
              return (
                <Fragment key={branch.id}>
                  <div
                    className={`branch-slide ${activeIdx === i ? 'is-active' : ''}`}
                    data-active={activeIdx === i ? 'true' : 'false'}
                    style={widthStyle}
                    onClick={activeIdx !== i ? () => setActiveIdx(i) : undefined}
                  >
                    <ConversationPanel
                      panelId={`branch-${i}`}
                      initialConversationId={branch.conversationId || branch.branchResult?.conversation.conversation_id}
                      initialConversation={branch.branchResult?.conversation || branch.initialConversation}
                      initialMessages={branch.branchResult?.messages}
                      initialBotId={i === 0 && !branch.conversationId ? (botIdParam || undefined) : undefined}
                      // Only the main panel, and only when it has no
                      // conversation yet: `?project=` means "the thread you are
                      // about to create belongs to this project". A branch
                      // inherits its project from its parent server-side.
                      projectId={i === 0 && !branch.conversationId ? (projectIdParam || undefined) : undefined}
                      threadMode={i === 0 && !branch.conversationId ? threadModeParam : undefined}
                      branchPointMessageId={branch.branchPointMessageId}
                      onBranchCreated={() => {/* handled via window event */}}
                      // Two distinct concerns derived from the same index:
                      //   • isBranchPanel — behavioural flag (skip auto-send, etc.)
                      //   • panelLabel    — pre-localized header label for split view
                      // Kept separate so non-rail callers can use one without the other.
                      isBranchPanel={i > 0}
                      isSplitView={isSplitView}
                      // Label rules:
                      //   • Main (i === 0)        → undefined → panel shows its own agent name
                      //   • Standalone (sidebar)  → undefined → panel shows its own agent name
                      //     (the user is comparing different chats, each should
                      //      identify by its agent — not "Branch N")
                      //   • Real fork branches    → "Branch 1", "Branch 2", …
                      panelLabel={(i === 0 || branch.isStandalone) ? undefined : branch.label}
                      // Every panel in split view gets the X — Main included.
                      // Closing Main promotes the leftmost branch into its
                      // place; data is unaffected (the conversation stays in
                      // the backend, just leaves the visible panel rail).
                      onClose={() => handleClosePanel(i)}
                      onNavigate={handleNavigate}
                      // Only the panel that owns the target conversation gets
                      // the request, so a jump can never scroll the wrong pane.
                      focusMessageId={
                        focusRequest && (branch.conversationId
                          || branch.branchResult?.conversation.conversation_id) === focusRequest.conversationId
                          ? focusRequest.messageId
                          : undefined
                      }
                      focusNonce={focusRequest?.nonce}
                      branchDisabled={branchDisabled}
                      branchDisabledReason={branchDisabledReason}
                    />
                  </div>
                  {/* Resize divider — sits BETWEEN adjacent panels (so render
                      one fewer than total panels). Only meaningful in split
                      view; CSS hides it on mobile. */}
                  {isSplitView && i < branches.length - 1 && (
                    <div
                      className="branch-divider"
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={t('chat.resizePanels') || 'Resize panels'}
                      onMouseDown={(e) => beginDividerDrag(i, e)}
                    >
                      <span className="branch-divider-grip" aria-hidden="true" />
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
